<template>
    <div class="dashboard">
        <div class="row mb-4 gy-3">
            <div class="col-md-12">
                <h5 class="text-primary mb-4">Hi, {{ activeUser.first_name }}</h5>
                <div class="card overflow-hidden">
                    <div class="card-body bg-marketplace d-flex"
                        :style="`background-image: url(${absoluteUrl('/images/bg/marketplace.png')}); background-size: cover;`">
                        <div class="flex-grow-1">
                            <h4 class="fs-4 fw-bold lh-base mb-0">Welcome to the <span class="text-success">Arise Youth
                                    Employment Portal.</span> </h4>
                            <p class="mb-0 mt-2 pt-1 text-muted">A centralized platform designed to connect youths of
                                Akwa Ibom with employment opportunities.</p>
                            <div class="d-grid d-sm-flex gap-3 mt-4">
                                <router-link to="/career-profile" class="btn btn-primary">Your Career Profile
                                </router-link>
                                <router-link to="/open-opportunities" class="btn btn-success">Explore Open
                                    Jobs</router-link>
                            </div>
                        </div>
                        <img style="width: 8rem;" class="d-none d-sm-inline"
                            :src="absoluteUrl('/images/svg/laptop-and-handshake.svg')">
                    </div>
                </div>
            </div>
            <div v-if="activeUser.id && !activeUser.is_verified" class="col-lg-12">
                <user-email-verification />
            </div>
        </div>
        <div>
            <CommonSpinner v-if="isLoading" />
            <div v-else>
                <div class="row gy-4">
                    <div v-if="dashboardData.candidate" class="col-lg-4">
                        <div class="card mb-0 bg-primary text-white card-animate">
                            <div class="card-body text-center">
                                <div class="d-flex justify-content-center align-items-center">
                                    <profile-level-apex :figure="dashboardData.candidate.profile_completion"
                                        :total="100" />
                                </div>
                                <div>Profile Completion Score</div>
                            </div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-sm-6">
                        <div class="card mb-0 h-100 card-animate">
                            <div class="card-body">
                                <div class="d-flex align-items-center">
                                    <div class="flex-grow-1 overflow-hidden">
                                        <p class="text-uppercase fw-medium text-muted text-truncate mb-0">
                                            Your Applications
                                        </p>
                                    </div>
                                </div>
                                <div class="d-flex align-items-end justify-content-between mt-4">
                                    <div>
                                        <h5 class="fs-22 fw-semibold ff-secondary mb-4">
                                            {{ dashboardData.opportunities.matched }}
                                        </h5>
                                        <router-link to="/applications" class="small text-decoration-underline">Your
                                            Applications</router-link>
                                    </div>
                                    <div class="avatar-sm flex-shrink-0">
                                        <span class="avatar-title bg-soft-primary rounded fs-3">
                                            <i class="ri-sort-desc text-primary"></i>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-sm-6">
                        <div class="card mb-0 h-100 card-animate">
                            <div class="card-body">
                                <div class="d-flex align-items-center">
                                    <div class="flex-grow-1 overflow-hidden">
                                        <p class="text-uppercase fw-medium text-muted text-truncate mb-0">
                                            Open Opportunities
                                        </p>
                                    </div>
                                </div>
                                <div class="d-flex align-items-end justify-content-between mt-4">
                                    <div>
                                        <h4 class="fs-22 fw-semibold ff-secondary mb-4">
                                            {{ dashboardData.opportunities.open }}
                                        </h4>
                                        <router-link to="/open-opportunities"
                                            class="small text-decoration-underline">View Open Opportunites</router-link>
                                    </div>
                                    <div class="avatar-sm flex-shrink-0">
                                        <span class="avatar-title bg-soft-info rounded fs-3">
                                            <i class="mdi mdi-archive-search-outline text-info"></i>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="dashboardData.ask_to_complete_profile" class="row gy-4 mt-4">
                    <div class="col-12">
                        <div class="card mb-0">
                            <div class="alert alert-warning border-0 rounded-0 m-0 d-flex align-items-center"
                                role="alert">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="feather feather-alert-triangle text-warning me-2 icon-sm">
                                    <path
                                        d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z">
                                    </path>
                                    <line x1="12" y1="9" x2="12" y2="13"></line>
                                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                </svg>
                                <div class="flex-grow-1 text-truncate">
                                    Your profile is not complete.
                                </div>
                                <div class="flex-shrink-0">
                                    <router-link to="/career-profile"
                                        class="text-reset text-decoration-underline"><b>Continue</b></router-link>
                                </div>
                            </div>
                            <div class="card-body d-flex flex-column justify-content-between">
                                <div class="row gy-4 gx-5">
                                    <div class="col-lg-7">
                                        <ul class="ps-3">
                                            <li v-for="(log, lIndex) in dashboardData.incomplete_profile_logs"
                                                :key="lIndex" class="mb-2"><strong>{{ log.title }}</strong> -
                                                <router-link :to="log.url">{{ log.description }}</router-link></li>
                                        </ul>
                                    </div>
                                    <div class="col-lg-5 text-lg-center">
                                        <p class="fs-16 mb-3 lh-base">Complete your profile for the best chance to be
                                            matched with <span class="fw-semibold">opportunities!</span></p>
                                        <div>
                                            <router-link to="/career-profile"
                                                class="btn btn-primary"><b>Continue</b></router-link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mt-4">
                    <opportunities />
                </div>
                <div class="mt-4">
                    <notification-list />
                </div>
            </div>
            <b-modal size="lg" v-model="forceUpdateProfile" hide-footer centered title="Update your profile"
                :no-close-on-backdrop="true" :no-close-on-esc="true" hide-header-close title-class="fs-6"
                @hide="initializeAll()">
                <p class="text-danger">Please take a moment to complete your profile.</p>
                <complete-profile-form v-if="forceUpdateProfile" :editItem="dashboardData.user"
                    @closeMe="closeForceUpdate()" />
            </b-modal>

            <b-modal v-model="forceUpdateMaritalStatus" hide-footer centered title="Update marital status"
                :no-close-on-backdrop="true" :no-close-on-esc="true" hide-header-close title-class="fs-6"
                @hide="initializeAll()">
                <p class="text-danger">Please update you marital status.</p>
                <update-marital-status-form v-if="forceUpdateMaritalStatus" :editItem="dashboardData.user"
                    @closeMe="closeForceUpdate()" />
            </b-modal>
        </div>
    </div>
</template>

<script>

import NotificationList from '@/views/notifications/NotificationList.vue'
import UserEmailVerification from "@/components/UserEmailVerification.vue"
import IsLoading from '@/components/IsLoading.vue'
import ProfileLevelApex from '@/components/widgets/ProfileLevelApex.vue'
import CompleteProfileForm from '@/components/forms/CompleteProfileForm.vue'
import UpdateMaritalStatusForm from '@/components/forms/UpdateMaritalStatusForm.vue'
import Opportunities from '@/views/opportunities/Opportunities.vue'

export default {
    name: 'dashboard',
    components: {
        IsLoading,
        UserEmailVerification,
        ProfileLevelApex,
        CompleteProfileForm,
        NotificationList,
        UpdateMaritalStatusForm,
        Opportunities
    },
    data() {
        return {
            isLoading: false,
            forceUpdateProfile: false,
            forceUpdateMaritalStatus: false
        }
    },
    computed: {
        activeUser() {
            return this.$store.state.AppActiveUser;
        },
        dashboardData() {
            return this.$store.state.dashboardData
        },
    },
    methods: {
        showProfileImg(image) {
            const data = { index: 0, visible: true, files: [this.absoluteUrl(image)] }
            this.$store.commit('TOGGLE_LIGHT_BOX', data)
        },
        fetchDashboardData() {
            if (!this.dashboardData.candidate.id) {
                this.isLoading = true
            }
            this.$store.dispatch('fetchDashboardData')
                .then((response) => {
                    this.isLoading = false
                    if (response.data.success) {
                        this.forceProfileUpdate(response.data.data);
                    }
                })
        },
        forceProfileUpdate(data) {
            this.forceUpdateProfile = data.force_profile_update
            this.forceUpdateMaritalStatus = data.force_update_marital_status
        },
        closeForceUpdate() {
            this.fetchDashboardData()
            this.initializeAll()
        },
        initializeAll() {
            this.forceUpdateProfile = false;
            this.forceUpdateMaritalStatus = false;
        },
    },
    created() {
        this.fetchDashboardData()
    }
}
</script>

<style scoped>
.user-photo {
    width: 9rem;
}

@media(max-width:567px) {
    .user-photo {
        width: 4rem;
    }
}

.dashboard .table.profile-table td,
.dashboard .table.profile-table th {
    padding: 0.1rem 0.2rem;
}
</style>
